import { Component, Input, ViewChild, ViewEncapsulation, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { MaterialModule } from '@app/material.module';

/**
 * Selector de hora en formato 24 horas con "rueda" de dos columnas
 * (00-23 : 00-59), siempre en 24 h sin importar el locale del navegador
 * (el input nativo type=time se muestra con AM/PM en algunos sistemas).
 *
 * Funciona con ngModel y con formControlName (ControlValueAccessor); el
 * valor es un string 'HH:mm' o null. Elegir los minutos cierra el panel.
 */
@Component({
  standalone: true,
  selector: 'app-time-wheel',
  imports: [MaterialModule],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TimeWheelComponent), multi: true },
  ],
  template: `
    <mat-form-field class="time-wheel-field">
      <mat-label>{{ label }}</mat-label>
      <input matInput readonly [value]="value ?? ''" [disabled]="disabled"
             [matMenuTriggerFor]="panel" (menuOpened)="onOpened()" placeholder="--:--">
      <mat-menu #panel="matMenu" class="time-wheel-panel">
        <div class="time-wheel" (click)="$event.stopPropagation()">
          <div class="time-wheel__col" data-wheel="h">
            @for (h of hours; track h) {
              <button type="button" [class.time-wheel__sel]="h === hourPart" (click)="setHour(h)">{{ h }}</button>
            }
          </div>
          <div class="time-wheel__col" data-wheel="m">
            @for (m of minutes; track m) {
              <button type="button" [class.time-wheel__sel]="m === minutePart" (click)="setMinute(m)">{{ m }}</button>
            }
          </div>
        </div>
      </mat-menu>
    </mat-form-field>
  `,
  styles: [`
    .time-wheel-field { width: 100%; }
    .time-wheel-field input { cursor: pointer; }

    .time-wheel-panel .mat-mdc-menu-content { padding: 0; }
    .time-wheel { display: flex; }
    .time-wheel__col {
      height: 210px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      min-width: 64px;
    }
    .time-wheel__col + .time-wheel__col { border-left: 1px solid #eceff1; }
    .time-wheel__col button {
      border: none;
      background: none;
      padding: 8px 18px;
      font: inherit;
      cursor: pointer;
      color: #37474f;
    }
    .time-wheel__col button:hover { background: #f5f7f8; }
    .time-wheel__col button.time-wheel__sel {
      background: #e3f2fd;
      color: #1565c0;
      font-weight: 600;
    }
  `],
})
export class TimeWheelComponent implements ControlValueAccessor {
  @Input() label = 'Hora';

  @ViewChild(MatMenuTrigger) trigger?: MatMenuTrigger;

  public readonly hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  public readonly minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  public value: string | null = null;
  public disabled = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  public get hourPart(): string | null { return this.value?.slice(0, 2) ?? null; }
  public get minutePart(): string | null { return this.value?.slice(3, 5) ?? null; }

  setHour(h: string): void {
    this.value = `${h}:${this.minutePart ?? '00'}`;
    this.onChange(this.value);
    this.onTouched();
  }

  setMinute(m: string): void {
    this.value = `${this.hourPart ?? '00'}:${m}`;
    this.onChange(this.value);
    this.onTouched();
    this.trigger?.closeMenu(); // elegir minutos completa la hora
  }

  /** Centra las columnas en el valor seleccionado al abrir el panel. */
  onOpened(): void {
    setTimeout(() => {
      document.querySelectorAll('.time-wheel .time-wheel__sel').forEach(el => {
        el.scrollIntoView({ block: 'center' });
      });
    });
  }

  // ── ControlValueAccessor ──
  writeValue(value: any): void {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(value ?? ''));
    this.value = match ? `${match[1].padStart(2, '0')}:${match[2]}` : null;
  }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
}
